import { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Plus, FileText, Search, Filter, Trash2, Eye, CheckCircle, AlertTriangle, ToggleLeft, ToggleRight, MessageSquare, Settings } from "lucide-react";

interface Document {
  id: string;
  title: string;
  file_name?: string;
  category: string;
  processing_status: string;
  total_chunks: number;
  created_at: string;
  type: 'document' | 'manual';
}

interface ManualEntry {
  id: string;
  title: string;
  category: string;
  content: string;
  processing_status: string;
  total_chunks: number;
  created_at: string;
}

interface AIChatLog {
  id: string;
  user_id?: string;
  username?: string;
  question: string;
  ai_response: string;
  is_accurate: boolean;
  flagged_by_admin?: string;
  created_at: string;
}

interface AIOverride {
  id: string;
  keyword: string;
  override_response: string;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

interface AISetting {
  id: string;
  setting_key: string;
  setting_value: boolean;
  updated_by?: string;
  updated_at: string;
}

export const MadisonKnowledgeBase = () => {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showManualDialog, setShowManualDialog] = useState(false);

  // AI Management state
  const [aiChatLogs, setAiChatLogs] = useState<AIChatLog[]>([]);
  const [aiOverrides, setAiOverrides] = useState<AIOverride[]>([]);
  const [aiSettings, setAiSettings] = useState<AISetting[]>([]);
  const [newOverrideKeyword, setNewOverrideKeyword] = useState('');
  const [newOverrideResponse, setNewOverrideResponse] = useState('');

  // Manual entry form state
  const [manualTitle, setManualTitle] = useState('');
  const [manualCategory, setManualCategory] = useState('general');
  const [manualContent, setManualContent] = useState('');

  const categories = [
    { value: 'immigration', label: 'Immigration' },
    { value: 'dmv', label: 'DMV' },
    { value: 'transit', label: 'Transit' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'general', label: 'General' }
  ];

  useEffect(() => {
    loadDocuments();
    loadAIData();
  }, []);

  const loadAIData = async () => {
    try {
      // Fetch AI data
      const [aiChatLogsResponse, aiOverridesResponse, aiSettingsResponse] = await Promise.all([
        supabase.from('ai_chat_logs').select('*').order('created_at', { ascending: false }).limit(50),
        supabase.from('ai_overrides').select('*').order('created_at', { ascending: false }),
        supabase.from('ai_settings').select('*')
      ]);

      setAiChatLogs(aiChatLogsResponse.data || []);
      setAiOverrides(aiOverridesResponse.data || []);
      setAiSettings(aiSettingsResponse.data || []);

      // Initialize default AI settings if none exist
      if (!aiSettingsResponse.data?.length) {
        await initializeAISettings();
      }
    } catch (error) {
      console.error('Error loading AI data:', error);
    }
  };

  const initializeAISettings = async () => {
    try {
      const defaultSettings = [
        { setting_key: 'promotional_responses_enabled', setting_value: true }
      ];

      for (const setting of defaultSettings) {
        await supabase.from('ai_settings').insert(setting);
      }
      
      // Refresh settings after initialization
      const { data } = await supabase.from('ai_settings').select('*');
      setAiSettings(data || []);
    } catch (error) {
      console.error('Error initializing AI settings:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      
      // Load documents
      const { data: docsData, error: docsError } = await supabase
        .from('madison_documents')
        .select('*')
        .order('created_at', { ascending: false });

      // Load manual entries
      const { data: manualData, error: manualError } = await supabase
        .from('madison_manual_entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (docsError) throw docsError;
      if (manualError) throw manualError;

      // Combine and format data
      const combinedData: Document[] = [
        ...(docsData || []).map(doc => ({ ...doc, type: 'document' as const })),
        ...(manualData || []).map(manual => ({ ...manual, type: 'manual' as const, file_name: undefined }))
      ];

      combinedData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setDocuments(combinedData);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        title: "Error",
        description: "Failed to load documents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload PDF, DOCX, XLSX, or TXT files only",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);

      // Generate unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `documents/${fileName}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('madison_kb')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Save document metadata
      const { data: documentData, error: insertError } = await supabase
        .from('madison_documents')
        .insert({
          title: file.name.replace(/\.[^/.]+$/, ""),
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          category: 'general',
          processing_status: 'pending'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: "File uploaded successfully",
        description: "Document is being processed...",
      });

      // Process the document
      await processDocument(documentData.id, 'document');
      await loadDocuments();

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload file",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleManualSubmit = async () => {
    if (!manualTitle.trim() || !manualContent.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setProcessing(true);

      // Save manual entry
      const { data: entryData, error: insertError } = await supabase
        .from('madison_manual_entries')
        .insert({
          title: manualTitle.trim(),
          category: manualCategory,
          content: manualContent.trim(),
          processing_status: 'pending'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: "Manual entry saved",
        description: "Entry is being processed...",
      });

      // Process the manual entry
      await processDocument(entryData.id, 'manual');
      
      // Reset form and close dialog
      setManualTitle('');
      setManualCategory('general');
      setManualContent('');
      setShowManualDialog(false);
      
      await loadDocuments();

    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Save failed",
        description: "Failed to save manual entry",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const processDocument = async (documentId: string, type: 'document' | 'manual') => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const { error } = await supabase.functions.invoke('process-madison-document', {
        body: { documentId, type },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

    } catch (error) {
      console.error('Processing error:', error);
      toast({
        title: "Processing failed",
        description: "Failed to process document",
        variant: "destructive",
      });
    }
  };

  // AI Management functions
  const handleMarkResponseAccuracy = async (logId: string, isAccurate: boolean) => {
    try {
      await supabase
        .from('ai_chat_logs')
        .update({ is_accurate: isAccurate })
        .eq('id', logId);

      setAiChatLogs(prev => 
        prev.map(log => 
          log.id === logId ? { ...log, is_accurate: isAccurate } : log
        )
      );

      toast({
        title: "Success",
        description: "Accuracy updated",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Error updating accuracy",
        variant: "destructive",
      });
    }
  };

  const handleAddOverride = async () => {
    if (!newOverrideKeyword.trim() || !newOverrideResponse.trim()) return;

    try {
      const { data } = await supabase
        .from('ai_overrides')
        .insert({
          keyword: newOverrideKeyword.trim(),
          override_response: newOverrideResponse.trim(),
          is_active: true
        })
        .select()
        .single();

      if (data) {
        setAiOverrides(prev => [data, ...prev]);
        setNewOverrideKeyword('');
        setNewOverrideResponse('');
        toast({
          title: "Success",
          description: "Manual override added",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Error adding override",
        variant: "destructive",
      });
    }
  };

  const handleToggleOverride = async (overrideId: string, currentStatus: boolean) => {
    try {
      await supabase
        .from('ai_overrides')
        .update({ is_active: !currentStatus })
        .eq('id', overrideId);

      setAiOverrides(prev =>
        prev.map(override =>
          override.id === overrideId
            ? { ...override, is_active: !currentStatus }
            : override
        )
      );

      toast({
        title: "Success",
        description: "Status updated",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Error updating status",
        variant: "destructive",
      });
    }
  };

  const handleDeleteOverride = async (overrideId: string) => {
    try {
      await supabase
        .from('ai_overrides')
        .delete()
        .eq('id', overrideId);

      setAiOverrides(prev => prev.filter(override => override.id !== overrideId));
      toast({
        title: "Success",
        description: "Override deleted",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Error deleting override",
        variant: "destructive",
      });
    }
  };

  const handleToggleAISetting = async (settingKey: string, currentValue: boolean) => {
    try {
      const existingSetting = aiSettings.find(s => s.setting_key === settingKey);
      
      if (existingSetting) {
        await supabase
          .from('ai_settings')
          .update({ setting_value: !currentValue })
          .eq('setting_key', settingKey);
      } else {
        await supabase
          .from('ai_settings')
          .insert({ setting_key: settingKey, setting_value: !currentValue });
      }

      const { data } = await supabase.from('ai_settings').select('*');
      setAiSettings(data || []);

      toast({
        title: "Success",
        description: "Setting updated",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Error updating setting",
        variant: "destructive",
      });
    }
  };

  const getPromotionalSetting = () => {
    const setting = aiSettings.find(s => s.setting_key === 'promotional_responses_enabled');
    return setting?.setting_value ?? true;
  };

  const deleteDocument = async (id: string, type: 'document' | 'manual') => {
    try {
      if (type === 'document') {
        const { error } = await supabase
          .from('madison_documents')
          .delete()
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('madison_manual_entries')
          .delete()
          .eq('id', id);
        if (error) throw error;
      }

      toast({
        title: "Document deleted",
        description: "Document has been removed from the knowledge base",
      });

      await loadDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Delete failed",
        description: "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: "secondary",
      processing: "default",
      completed: "default",
      error: "destructive"
    } as const;

    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      error: "bg-red-100 text-red-800"
    };

    return (
      <Badge variant={variants[status as keyof typeof variants]} className={colors[status as keyof typeof colors]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;
    const matchesType = typeFilter === 'all' || doc.type === typeFilter;
    return matchesSearch && matchesCategory && matchesType;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Madison Knowledge Base</h2>
          <p className="text-muted-foreground">Manage documents, AI settings, and chat logs for Madison</p>
        </div>
      </div>

      <Tabs defaultValue="knowledge" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="knowledge" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Knowledge Base
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            AI Settings
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Chat Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="knowledge" className="space-y-6">{/* Upload and Add Sections */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Document Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Document for Madison
            </CardTitle>
            <CardDescription>
              Upload PDF, DOCX, XLSX files to enhance Madison's knowledge
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".pdf,.docx,.xlsx,.txt"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF, DOCX, XLSX files up to 10MB
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Manual Entry */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Manual Info for Madison
            </CardTitle>
            <CardDescription>
              Add text-based information directly to Madison's knowledge base
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Manual Information
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add Manual Information</DialogTitle>
                  <DialogDescription>
                    Enter information that Madison can reference when helping users
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="manual-title">Title</Label>
                    <Input
                      id="manual-title"
                      value={manualTitle}
                      onChange={(e) => setManualTitle(e.target.value)}
                      placeholder="Enter a descriptive title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="manual-category">Category</Label>
                    <Select value={manualCategory} onValueChange={setManualCategory}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="manual-content">Content</Label>
                    <Textarea
                      id="manual-content"
                      value={manualContent}
                      onChange={(e) => setManualContent(e.target.value)}
                      placeholder="Enter the information content..."
                      rows={8}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowManualDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleManualSubmit} disabled={processing}>
                      {processing ? 'Processing...' : 'Save Information'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Knowledge Base Content
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="document">Uploads</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Documents Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Chunks</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading documents...
                    </TableCell>
                  </TableRow>
                ) : filteredDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      No documents found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">
                        {doc.title}
                        {doc.file_name && (
                          <div className="text-xs text-muted-foreground">{doc.file_name}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {doc.type === 'document' ? 'Upload' : 'Manual'}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize">{doc.category}</TableCell>
                      <TableCell>{getStatusBadge(doc.processing_status)}</TableCell>
                      <TableCell>{doc.total_chunks || 0}</TableCell>
                      <TableCell>
                        {new Date(doc.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteDocument(doc.id, doc.type)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          {/* AI Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                AI Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Promotional Responses</h4>
                  <p className="text-sm text-muted-foreground">
                    Allow AI to include sponsored ads in responses
                  </p>
                </div>
                <Switch
                  checked={getPromotionalSetting()}
                  onCheckedChange={() => handleToggleAISetting('promotional_responses_enabled', getPromotionalSetting())}
                />
              </div>
            </CardContent>
          </Card>

          {/* Manual Overrides */}
          <Card>
            <CardHeader>
              <CardTitle>Manual Overrides</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    placeholder="Keyword..."
                    value={newOverrideKeyword}
                    onChange={(e) => setNewOverrideKeyword(e.target.value)}
                  />
                  <Input
                    placeholder="Custom response..."
                    value={newOverrideResponse}
                    onChange={(e) => setNewOverrideResponse(e.target.value)}
                  />
                  <Button onClick={handleAddOverride} disabled={!newOverrideKeyword || !newOverrideResponse}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Keyword</TableHead>
                      <TableHead>Response</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aiOverrides.map((override) => (
                      <TableRow key={override.id}>
                        <TableCell className="font-medium">{override.keyword}</TableCell>
                        <TableCell className="max-w-xs truncate">{override.override_response}</TableCell>
                        <TableCell>
                          <Badge variant={override.is_active ? 'default' : 'secondary'}>
                            {override.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleOverride(override.id, override.is_active)}
                            >
                              {override.is_active ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteOverride(override.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="space-y-6">
          {/* Chat Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                AI Chat Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead>AI Response</TableHead>
                    <TableHead>Accuracy</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aiChatLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.username || 'Anonymous'}</TableCell>
                      <TableCell className="max-w-xs truncate">{log.question}</TableCell>
                      <TableCell className="max-w-xs truncate">{log.ai_response}</TableCell>
                      <TableCell>
                        <Badge variant={log.is_accurate ? 'default' : 'destructive'}>
                          {log.is_accurate ? 'Accurate' : 'Inaccurate'}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(log.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkResponseAccuracy(log.id, true)}
                            disabled={log.is_accurate}
                          >
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkResponseAccuracy(log.id, false)}
                            disabled={!log.is_accurate}
                          >
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};